import { CalendarIcon, ChevronRightIcon } from '@heroicons/react/20/solid'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ResourceLoading } from '../../../utils/ResourceLoading'
import { ResourceError } from '../../../utils/ResourceError'
import { ResourceEmpty } from '../../../utils/ResourceEmpty'
import { useTranslation } from 'react-i18next'

function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}

function formatDate(dateString) {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleDateString('no-NO', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

function getStatusBadge(status, finished, active) {
  if (finished) {
    return (
      <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
        Fullført
      </span>
    )
  }
  if (active) {
    return (
      <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800">
        Aktiv
      </span>
    )
  }
  return (
    <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-800">
      Inaktiv
    </span>
  )
}

export default function InventoryCountsStacklist(props) {
  const { label, items, error, errors, isLoading, onCreateNew } = props
  const navigate = useNavigate()
  const { t } = useTranslation()

  const handleItemClick = (itemId) => {
    if (!itemId) return
    navigate(`/dashboard/inventory/counts/${itemId}`)
  }

  const renderItems = () => {
    if (!items || items.length === 0) return null

    // Sort by created_at descending to show latest first
    const sortedItems = [...items].sort((a, b) => {
      const dateA = new Date(a.created_at || a.updated_at || 0)
      const dateB = new Date(b.created_at || b.updated_at || 0)
      return dateB - dateA
    })

    return sortedItems.slice(0, 5).map((item) => (
      <li
        key={item.id}
        onClick={() => handleItemClick(item.id)}
        className="cursor-pointer transition-colors hover:bg-gray-50"
      >
        <div className="flex items-center px-4 py-4 sm:px-6">
          <div className="min-w-0 flex-1 sm:flex sm:items-center sm:justify-between">
            <div className="truncate">
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0">
                  {getStatusBadge(item.status, item.finished, item.active)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 text-sm">
                    <p className="truncate font-medium text-gray-900">
                      {item.title || item.count_number || `Telling #${item.id}`}
                    </p>
                    {item.count_number && item.title && (
                      <p className="flex-shrink-0 font-normal text-gray-500">#{item.count_number}</p>
                    )}
                  </div>
                  {item.description && (
                    <p className="mt-1 truncate text-sm text-gray-500">{item.description}</p>
                  )}
                  <div className="mt-2 flex flex-wrap items-center gap-4 text-xs text-gray-500">
                    {item.date && (
                      <div className="flex items-center">
                        <CalendarIcon className="mr-1.5 h-4 w-4 flex-shrink-0 text-gray-400" aria-hidden="true" />
                        <span>{formatDate(item.date)}</span>
                      </div>
                    )}
                    {item.created_at && (
                      <div className="flex items-center">
                        <span>Opprettet: {formatDate(item.created_at)}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="ml-5 flex-shrink-0">
            <ChevronRightIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
          </div>
        </div>
      </li>
    ))
  }

  return (
    <div className="overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-inset ring-gray-200">
      <div className="flex items-center justify-between border-b border-gray-200 bg-white px-4 py-4 sm:px-6">
        <h3 className="text-lg font-medium leading-6 text-gray-900">{label || 'Inventory Counts'}</h3>
        {onCreateNew && (
          <button
            type="button"
            onClick={onCreateNew}
            className="inline-flex items-center rounded-md bg-blue-400 px-3 py-1.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
          >
            Ny telling
          </button>
        )}
      </div>
      <div className="max-h-[400px] overflow-auto">
        <ul role="list" className="divide-y divide-gray-200">
          {isLoading ? (
            <div className="p-5">
              <ResourceLoading />
            </div>
          ) : error && errors ? (
            <div className="p-5">
              <ResourceError errors={errors} />
            </div>
          ) : items && items.length > 0 ? (
            renderItems()
          ) : (
            <div className="p-5">
              <ResourceEmpty />
            </div>
          )}
        </ul>
      </div>
    </div>
  )
}

